'use client';

import { faSync } from '@fortawesome/pro-light-svg-icons';
import { faAngleDown, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@pickleballinc/react-ui';
import { Menu } from '@pickleballinc/react-ui';
import getCDNURL from '@pickleballinc/ui/components/global/utils/getCDNURL';
import Link from 'next/link';
import { useState } from 'react';

import { LeaguePlayerRankingsDropdown } from '@/components/atoms/LeaguePlayerRankingsDropdown';
import { LeaguePlayerCardStats } from '@/components/molecules/LeaguePlayerCardStats';
import { PlayerTeamsModal } from '@/components/molecules/modals/PlayerTeamsModal';

interface PlayerCardProps {
	player: any;
	className?: string;
}

export function PlayerCard({ player, className }: PlayerCardProps) {
	const [isPersonalInfoDisplayed, setIsPersonalInfoDisplayed] = useState(true);
	const [isPlayerTeamsModalOpen, setIsPlayerTeamsModalOpen] = useState(false);

	const handleFlip = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsPersonalInfoDisplayed(!isPersonalInfoDisplayed);
	};

	const openModal = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsPlayerTeamsModalOpen(true);
	};

	return (
		<>
			<Link href={`#`}>
				<div
					className={`flip-card flex min-h-[370px] w-full rounded-xl border border-gray-200 bg-white ${!isPersonalInfoDisplayed && 'flipped'} ${className ?? ''}`}
				>
					<div className="flip-card-inner grow">
						{/* Front Side */}
						<div className="flip-card-front flex grow flex-col">
							<div>
								<div className="absolute end-0 top-0 flex cursor-pointer items-center gap-1 pe-4 pt-4" onClick={handleFlip}>
									<p className="text-xs font-normal leading-[18px] text-gray-500">Flip</p>
									<FontAwesomeIcon icon={faSync} className="size-3 text-gray-500" />
								</div>

								<img
									src={getCDNURL(player.userInfo.profilePictureUrl, 80, 80)}
									alt="Profile Picture"
									className="mx-auto mt-4 size-20 rounded-full border border-gray-300 bg-gray-200"
								/>
							</div>
							<div className="mt-3 flex flex-col gap-1.5 px-4">
								<span className="inline-block text-base font-semibold text-gray-600">{player.userInfo.firstName}</span>

								<div className="flex flex-row items-center justify-between">
									<p className="text-2xl font-bold text-gray-800">{player.userInfo.lastName}</p>
									<div className="flex flex-col items-start justify-center">
										<p className="text-xs font-normal leading-[18px] text-gray-600">Age</p>
										<p className="text-sm font-bold text-gray-900">
											{!player.dob ? (
												<Tooltip placement="bottom">
													<Tooltip.Trigger asChild>
														<FontAwesomeIcon icon={faEyeSlash} className="ml-0.5" />
													</Tooltip.Trigger>
													<Tooltip.Content>
														<p>No age information available</p>
													</Tooltip.Content>
												</Tooltip>
											) : (
												player.dob
											)}
										</p>
									</div>
								</div>

								{player.listOfTeams.length > 0 && (
									<div onClick={openModal} className="flex cursor-pointer items-start gap-2">
										{player.listOfTeams[0].teamFlagUrl && (
											<img
												src={getCDNURL(player.listOfTeams[0].teamFlagUrl, 18, 18)}
												alt="Icon"
												className="size-[18px] rounded-full"
											/>
										)}

										<p className="text-sm font-semibold leading-5 text-blue-500">{player.listOfTeams[0].team}</p>
									</div>
								)}

								<span className="text-xs font-medium leading-[18px] text-gray-600">Right-handed</span>
							</div>

							<hr className="my-3 bg-gray-200" />

							<div className="flex flex-col gap-2 px-4 pb-4">
								<div className="flex gap-1 text-xs font-medium leading-[18px] text-gray-600">
									<span className="text-nowrap">Rankings |</span>

									<Menu showArrow={false} offset={5} placement="bottom-end">
										<Menu.Trigger asChild>
											<div className="flex flex-1 cursor-pointer justify-between gap-2">
												<span>Division 3.0 (+2)</span>
												<FontAwesomeIcon icon={faAngleDown} className="size-4 text-gray-900" />
											</div>
										</Menu.Trigger>

										<LeaguePlayerRankingsDropdown />
									</Menu>
								</div>
								<div className="flex flex-col gap-1.5 text-sm font-semibold leading-5 text-gray-900">
									<div className="flex items-center justify-between">
										{player.gender == 'M' ? <span>MS</span> : <span>WS</span>}
										<span>1st</span>
									</div>
									<div className="flex items-center justify-between">
										{player.gender == 'M' ? <span>MD</span> : <span>WD</span>}
										<span>2st</span>
									</div>
									<div className="flex items-center justify-between">
										{player.gender == 'M' ? <span>MX</span> : <span>WX</span>}
										<span>24th</span>
									</div>
								</div>
							</div>
						</div>

						{/* Back Side */}
						<div className="flip-card-back scrollbar-thin scrollbar-webkit flex grow flex-col gap-4 overflow-auto rounded-xl bg-white p-4">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-1 text-xs font-medium leading-[18px] text-gray-600">
									<span className="text-nowrap">Stats |</span>

									<Menu showArrow={false} offset={5} placement="bottom-end">
										<Menu.Trigger asChild>
											<div className="flex flex-1 cursor-pointer gap-3">
												<span>Division 3.0 (+2)</span>
												<FontAwesomeIcon icon={faAngleDown} className="size-4 text-gray-900" />
											</div>
										</Menu.Trigger>

										<LeaguePlayerRankingsDropdown />
									</Menu>
								</div>
								<div className="flex cursor-pointer items-center gap-1" onClick={handleFlip}>
									<p className="text-xs font-normal leading-[18px] text-gray-500">Flip</p>
									<FontAwesomeIcon icon={faSync} className="size-3 text-gray-500" />
								</div>
							</div>

							<div className="flex flex-col gap-2">
								<LeaguePlayerCardStats
									title={player.gender == 'M' ? 'MS' : 'WS'}
									GW="34"
									GL="22"
									WPercentage="11"
									PW="44"
									AvgOpponentRating="6.71"
									AvgPartnerRating="6.65"
								/>
								<LeaguePlayerCardStats
									title={player.gender == 'M' ? 'MD' : 'WD'}
									GW="14"
									GL="66"
									WPercentage="55"
									PW="83"
									AvgOpponentRating="4.41"
									AvgPartnerRating="4.20"
								/>
								<LeaguePlayerCardStats
									title={player.gender == 'M' ? 'MX' : 'WX'}
									GW="64"
									GL="72"
									WPercentage="42"
									PW="56"
									AvgOpponentRating="3.18"
									AvgPartnerRating="2.25"
								/>
							</div>
						</div>
					</div>
				</div>
			</Link>

			{isPlayerTeamsModalOpen && (
				<PlayerTeamsModal
					title={`${player.userInfo.firstName}’s teams`}
					onClose={() => setIsPlayerTeamsModalOpen(false)}
					listOfTeams={player.listOfTeams}
				/>
			)}
		</>
	);
}
